import { Button, Card, CardBody, CardTitle } from '@fold-and-flow/ui'
import { useForm } from '@tanstack/react-form'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { z } from 'zod'
import { trpc } from '#lib/trpc'
import { trimmedNonEmptyStringSchema } from '#lib/validation'

const journalFormSchema = z.object({
  content: trimmedNonEmptyStringSchema,
})

export function JournalPage() {
  const { sessionId } = useParams({ from: '/_app/journal/$sessionId' })
  const navigate = useNavigate()

  const createEntry = trpc.journal.create.useMutation({
    onSuccess: () => navigate({ to: '/' }),
  })

  const { data: existingEntry } = trpc.journal.bySession.useQuery({ sessionId })

  const form = useForm({
    defaultValues: {
      content: existingEntry?.content.trim() ?? 'Felt grounded.',
    } satisfies z.infer<typeof journalFormSchema>,
    validators: {
      onSubmit: journalFormSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = journalFormSchema.parse(value)

      await createEntry.mutateAsync({ sessionId, content: parsed.content })
    },
  })

  if (existingEntry) {
    return (
      <div className="max-w-md mx-auto flex flex-col gap-6">
        <div>
          <h1 className="font-serif text-2xl text-warm-gray-900 mb-1">Your reflection</h1>
          <p className="text-sm text-warm-gray-400">From this session</p>
        </div>
        <Card elevated>
          <CardBody>
            <p className="text-warm-gray-700 leading-relaxed whitespace-pre-wrap">
              {existingEntry.content}
            </p>
            {existingEntry.aiExtractedThemes.length > 0 && (
              <div className="mt-4 pt-4 border-t border-cream-200">
                <p className="text-xs text-warm-gray-400 mb-2">Themes we noticed</p>
                <div className="flex flex-wrap gap-1.5">
                  {existingEntry.aiExtractedThemes.map((t) => (
                    <span
                      key={t}
                      className="px-2.5 py-1 rounded-full bg-sage-400/10 text-sage-600 text-xs"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
        <Link
          to="/"
          className="text-center text-sm text-warm-gray-400 hover:text-warm-gray-600 transition-colors"
        >
          ← Back to home
        </Link>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-md mx-auto"
    >
      <Card elevated>
        <CardBody>
          <CardTitle className="mb-1">How did that feel?</CardTitle>
          <p className="text-sm text-warm-gray-400 mb-6">
            Notice anything in your body or mind? No wrong answers.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              void form.handleSubmit()
            }}
            className="flex flex-col gap-6"
          >
            <form.Field name="content">
              {(field) => (
                <textarea
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Write here…"
                  rows={6}
                  className="w-full rounded-xl border border-cream-200 bg-white px-4 py-3 text-sm text-warm-gray-800 placeholder:text-warm-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-terracotta-400"
                />
              )}
            </form.Field>

            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="text-sm text-warm-gray-400 hover:text-warm-gray-600 transition-colors"
              >
                Skip for now
              </Link>
              <form.Subscribe
                selector={(s) => ({ isSubmitting: s.isSubmitting, content: s.values.content })}
              >
                {({ isSubmitting, content }) => (
                  <Button type="submit" loading={isSubmitting} disabled={!content.trim()}>
                    Save & done
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </form>
        </CardBody>
      </Card>
    </motion.div>
  )
}
